import i18n from 'i18next';
import type { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import {
  SUPPORTED_LANGUAGES,
  RTL_LANGUAGES,
  type SupportedLanguage,
} from '@careertree/shared';

// Re-export for convenience
export { SUPPORTED_LANGUAGES, RTL_LANGUAGES, type SupportedLanguage };

const WEB_SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (language): language is Exclude<SupportedLanguage, 'ko'> => language !== 'ko'
);

/**
 * Namespace list.
 */
export const NAMESPACES = [
  'common',
  'graph',
  'landing',
  'auth',
  'knowledge',
  'publication',
  'profile',
  'persona',
  'survey',
  'timeline',
  'feedback',
  'chat',
  'tour',
  'mission',
  'skills',
] as const;

export type Namespace = typeof NAMESPACES[number];

/**
 * Locale loader using Vite dynamic imports.
 * Creates chunks by language and namespace at build time.
 */
const localeLoader = resourcesToBackend(
  (language: string, namespace: string) =>
    import(`../locales/${language}/${namespace}.json`)
);

i18n
  .use(LanguageDetector)
  .use(localeLoader)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: [...WEB_SUPPORTED_LANGUAGES],
    defaultNS: 'common',
    ns: NAMESPACES,

    // Language-only mode: converts "en-US" to "en".
    load: 'languageOnly',

    interpolation: {
      escapeValue: false,
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      // Store only the language code in localStorage.
      convertDetectedLanguage: (lng: string) => {
        const detectedLanguage = lng.split('-')[0];
        const languageOnly = (WEB_SUPPORTED_LANGUAGES as readonly string[]).includes(detectedLanguage)
          ? detectedLanguage
          : 'en';
        if (import.meta.env.DEV) {
          console.log('[i18n] Detected language:', lng, '→', languageOnly);
        }
        return languageOnly;
      },
    },

    debug: import.meta.env.DEV,

    react: {
      useSuspense: true,
    },
  } satisfies InitOptions);

/**
 * Update HTML lang/dir attributes.
 * RTL languages use dir="rtl"; all others use dir="ltr".
 */
function updateDocumentDirection(lng: string) {
  const language = lng.split('-')[0] as SupportedLanguage;
  document.documentElement.lang = language;
  document.documentElement.dir = (RTL_LANGUAGES as readonly string[]).includes(language) ? 'rtl' : 'ltr';
}

// Apply on initial load.
if (typeof document !== 'undefined') {
  updateDocumentDirection(i18n.language || 'en');
}

// Apply on language change.
i18n.on('languageChanged', updateDocumentDirection);

export { i18n };
